/**
 * <ClientsBrandsSubTab /> — tabular view of every brand across all clients
 * in the workspace, on Clients > Brands subtab.
 *
 * Assumes GET /workspaces/{ws}/brands returns all brands (visible ones for
 * the current user based on backend RBAC).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";
import { Gem } from "lucide-react";

const STATUS_TONE = {
  active: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  paused: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  archived: "border-border text-muted-foreground bg-secondary/40",
};

export default function ClientsBrandsSubTab() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      const [b, c] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/brands`),
        api.get(`/workspaces/${activeWorkspaceId}/clients`),
      ]);
      const cmap = {};
      (c.data || []).forEach((x) => { cmap[x.id] = x; });
      setClientsMap(cmap);
      // Some backends return {brands: [...]} — normalise
      const list = Array.isArray(b.data) ? b.data : (b.data?.brands || []);
      setBrands(list);
      setLoaded(true);
    })();
  }, [activeWorkspaceId]);

  const rows = useMemo(() => brands.map((br) => ({
    ...br,
    client_name: clientsMap[br.client_id]?.name || "—",
  })), [brands, clientsMap]);

  const columns = [
    { key: "name", label: "Brand", width: 240, sortable: true, filterType: "text", frozen: true,
      render: (r) => <span className="font-medium truncate">{r.name}</span> },
    { key: "client_name", label: "Client", width: 200, sortable: true, filterType: "text", groupable: true },
    { key: "status", label: "Status", width: 110, sortable: true, filterType: "select",
      filterOptions: [
        { value: "active", label: "Active" }, { value: "paused", label: "Paused" },
        { value: "archived", label: "Archived" },
      ], groupable: true,
      render: (r) => (
        <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${STATUS_TONE[r.status] || ""}`}>
          {r.status || "active"}
        </span>
      ),
    },
    { key: "industry", label: "Industry", width: 160, sortable: true, filterType: "text", groupable: true,
      render: (r) => <span className="text-xs">{r.industry || "—"}</span> },
    { key: "website", label: "Website", width: 220, sortable: true, filterType: "text",
      render: (r) => r.website ? (
        <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate" onClick={(e) => e.stopPropagation()}>
          {r.website.replace(/^https?:\/\//, "")}
        </a>
      ) : <span className="text-xs text-muted-foreground">—</span>,
    },
    { key: "created_at", label: "Added", width: 120, sortable: true, align: "right",
      render: (r) => <span className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</span> },
  ];

  return (
    <AdvancedTable
      tableKey="clients-brands"
      title="All brands"
      rows={rows}
      columns={columns}
      loading={!loaded}
      emptyIcon={Gem}
      emptyTitle="No brands yet"
      emptyDescription="Brands associated with any client will list here."
      onRowClick={(r) => navigate(`/app/brands/${r.id}`)}
    />
  );
}
